import { Link } from "@tanstack/react-router"
import { type FormEvent, useState } from "react"
import { AuthLayout } from "@/components/auth-layout"
import { ErrorText } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { Field } from "@/components/ui/field"
import { resetRequestErrorText } from "@/features/settings/email-words"
import { requestPasswordReset } from "@/lib/email-api"
import { forgotPasswordRoute } from "@/router"

/**
 * `/forgot-password` (wave M spec §6.5): one field, then "Check your email" with the address as typed (a typo is the
 * usual reason no mail arrives; it's the person's own input on their own screen, so nothing to mask) and a way back
 * to the form with the field filled in.
 */
export function ForgotPasswordPage() {
  const { redirect } = forgotPasswordRoute.useSearch()
  const [sentTo, setSentTo] = useState<string | null>(null)
  const [typed, setTyped] = useState("")
  const [error, setError] = useState<string | null>(null)
  const [pending, setPending] = useState(false)
  const back = (
    <Link to="/sign-in" search={redirect ? { redirect } : {}} className="font-medium link">
      Back to sign in
    </Link>
  )

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const asTyped = String(new FormData(event.currentTarget).get("email") ?? "").trim()
    const email = asTyped.toLowerCase()
    if (!email) return
    setTyped(asTyped)
    setPending(true)
    setError(null)
    try {
      const failed = await requestPasswordReset(email)
      if (failed) setError(resetRequestErrorText(failed.status))
      else setSentTo(asTyped)
    } catch {
      setError("Couldn't reach Minga. Try again.")
    } finally {
      setPending(false)
    }
  }

  if (sentTo)
    return (
      <AuthLayout
        title="Check your email"
        description="If there's a Minga account for this address, a link is on its way."
        footer={back}
      >
        <div className="grid gap-4">
          <p role="status" className="break-all font-mono text-[13px] text-ink">
            {sentTo}
          </p>
          <p className="text-sm text-ink-2">It works for 1 hour, and only the newest link works.</p>
          <Button variant="secondary" onClick={() => setSentTo(null)}>
            Use a different address
          </Button>
        </div>
      </AuthLayout>
    )

  return (
    <AuthLayout
      title="Reset your password"
      description="We'll email you a link to choose a new one."
      footer={back}
    >
      <form className="grid gap-4" onSubmit={onSubmit}>
        <Field
          id="email"
          label="Email"
          type="email"
          autoComplete="username"
          required
          defaultValue={typed}
          // back from "Use a different address": the field is filled in, focus it to fix the typo
          autoFocus={typed !== ""}
        />
        {error && <ErrorText>{error}</ErrorText>}
        <Button type="submit" disabled={pending}>
          {pending ? "Sending…" : "Email me a link"}
        </Button>
      </form>
    </AuthLayout>
  )
}
